/**
 * CR323: Ribbon Composer
 * CR325: Now uses shared ribbonViewModel helper for consistency with v2 renderer
 * 
 * Polished UI for configuring ribbon content + typography.
 * Replaces the fragmented "display intent" toggles with a coherent composer.
 * 
 * Features:
 * - Live ribbon preview with current sample deal
 * - Primary line (deal type label + amount)
 * - Optional merchant subline (logo + name)
 * - Typography controls with inheritance option
 * - Visibility status chip (Structure controls visibility)
 */

import { useMemo, useCallback } from "react";
import { useDebouncedNullableInput } from "@/hooks/useDebouncedInput";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Ribbon,
  Info,
  CheckCircle2,
  XCircle,
  Type,
} from "lucide-react";
import type { Deal } from "@/lib/types/deals";
import { 
  buildRibbonViewModel, 
  getMerchantSublineName,
  type RibbonComponentConfig,
} from "@/lib/deal-cards/ribbonViewModel";
import { cn } from "@/lib/utils";

// ================================================================
// TYPES
// ================================================================

export interface RibbonPrimaryConfig {
  showDealTypeLabel: boolean;
  labelMode: "default" | "custom";
  labelOverride: string | null;
  showAmount: boolean;
  separator: "space" | "bullet" | "dash";
  amountMode?: "reward_amount" | "percent_off" | "points" | "computed_value";
  // CR335: Rate qualifier control for POINTS deals ("per $X")
  rateQualifier?: "auto" | "hide" | "compact";
}

export interface RibbonMerchantSublineConfig {
  enabled: boolean;
  showLogo: boolean;
  showName: boolean;
}

export interface RibbonTypography {
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
}

export interface RibbonComposerConfig {
  primary: RibbonPrimaryConfig;
  merchantSubline: RibbonMerchantSublineConfig;
  typography: RibbonTypography;
  merchantTypography: RibbonTypography | null;
  // Legacy compatibility
  position?: "top-left" | "top-right";
  offsetX?: number;
  offsetY?: number;
}

export const DEFAULT_RIBBON_COMPOSER_CONFIG: RibbonComposerConfig = {
  primary: {
    showDealTypeLabel: true,
    labelMode: "default",
    labelOverride: null,
    showAmount: true,
    separator: "space",
  },
  merchantSubline: {
    enabled: false,
    showLogo: true,
    showName: true,
  },
  typography: {
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 500,
  },
  merchantTypography: null,
  position: "top-right",
  offsetX: 0,
  offsetY: 0,
};

interface RibbonComposerProps {
  config: RibbonComposerConfig;
  onChange: (config: RibbonComposerConfig) => void;
  /** Whether ribbon is enabled in Structure (visibility) */
  structureEnabled: boolean;
  /** Optional sample deal for live preview (raw data from DB) */
  sampleDeal?: Record<string, unknown> | null;
  /** Deal type key for default label derivation */
  dealTypeKey: string;
  /** CR337: Resolved ribbon fill color HEX for preview */
  ribbonColorHex?: string | null;
}

// Font family options
const FONT_FAMILIES = [
  { value: "inherit", label: "Default (inherit)" },
  { value: "Inter", label: "Inter" },
  { value: "DM Sans", label: "DM Sans" },
  { value: "Space Grotesk", label: "Space Grotesk" },
  { value: "Manrope", label: "Manrope" },
  { value: "Montserrat", label: "Montserrat" },
];

const FONT_WEIGHTS = [
  { value: 400, label: "Regular (400)" },
  { value: 500, label: "Medium (500)" },
  { value: 600, label: "Semibold (600)" },
  { value: 700, label: "Bold (700)" },
];

const SEPARATOR_OPTIONS = [
  { value: "space", label: "Space", display: " " },
  { value: "bullet", label: "Bullet (•)", display: " • " },
  { value: "dash", label: "Dash (—)", display: " — " },
];

// CR326: RATE_QUALIFIER_OPTIONS removed - POINTS now uses simple "{amount} {label}" format

// ================================================================
// HELPERS
// ================================================================

function VisibilityChip({ enabled }: { enabled: boolean }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant={enabled ? "default" : "secondary"} 
            className={cn(
              "text-[10px] px-1.5 py-0 h-5",
              enabled ? 'bg-green-600 hover:bg-green-600' : 'bg-muted text-muted-foreground'
            )}
          >
            {enabled ? (
              <><CheckCircle2 className="h-3 w-3 mr-0.5" /> On</>
            ) : (
              <><XCircle className="h-3 w-3 mr-0.5" /> Off</>
            )}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-[200px]">
          <p className="text-xs">Visibility controlled in Structure → Front</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * CR327: Debounced label input to prevent preview jitter during typing.
 */
function DebouncedLabelInput({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const { localValue, onChange: handleChange, onBlur, isPending } = useDebouncedNullableInput(
    value,
    onChange,
    { debounceMs: 250 }
  );
  
  return (
    <div className="relative">
      <Input
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={onBlur}
        placeholder="e.g., BONUS, % BACK, CASHBACK"
        className="h-8 text-xs"
      />
      {isPending && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <div className="w-1.5 h-1.5 rounded-full bg-primary/50 animate-pulse" />
        </div>
      )}
    </div>
  );
}

// Helper type for raw deal data (Record from DB)
type RawDealData = Record<string, unknown>;

/**
 * Build the rendered ribbon text from config and deal.
 * CR325: Now uses shared buildRibbonViewModel helper.
 */
export function buildRenderedRibbonText(
  config: RibbonComposerConfig,
  dealTypeKey: string,
  rawDeal: RawDealData | null | undefined
): string {
  const viewModel = buildRibbonViewModel(rawDeal, dealTypeKey, config as RibbonComponentConfig);
  return viewModel.primaryText;
}

// ================================================================
// COMPONENT
// ================================================================

export function RibbonComposer({
  config,
  onChange,
  structureEnabled,
  sampleDeal,
  dealTypeKey,
  ribbonColorHex,
}: RibbonComposerProps) {
  
  // Compute rendered output
  const renderedText = useMemo(() => {
    return buildRenderedRibbonText(config, dealTypeKey, sampleDeal);
  }, [config, dealTypeKey, sampleDeal]);
  
  // CR326: derivedAmount now uses the view model for consistency
  // This ensures POINTS shows numeric-only (no "pts" suffix)
  const derivedAmount = useMemo(() => {
    const viewModel = buildRibbonViewModel(sampleDeal, dealTypeKey, config as RibbonComponentConfig);
    return viewModel.amountFormatted;
  }, [sampleDeal, dealTypeKey, config]);
  
  const merchantSublineName = useMemo(() => {
    return getMerchantSublineName(sampleDeal);
  }, [sampleDeal]);
  
  // Effective merchant typography (inherit or custom)
  const effectiveMerchantTypo = config.merchantTypography ?? config.typography;
  const usesInheritedTypo = config.merchantTypography === null;
  
  // Update helpers
  const updatePrimary = (update: Partial<RibbonPrimaryConfig>) => {
    onChange({
      ...config,
      primary: { ...config.primary, ...update },
    });
  };
  
  const updateMerchantSubline = (update: Partial<RibbonMerchantSublineConfig>) => {
    onChange({
      ...config,
      merchantSubline: { ...config.merchantSubline, ...update },
    });
  };
  
  const updateTypography = (update: Partial<RibbonTypography>) => {
    onChange({
      ...config,
      typography: { ...config.typography, ...update },
    });
  };
  
  const updateMerchantTypography = (update: Partial<RibbonTypography> | null) => {
    if (update === null) {
      onChange({ ...config, merchantTypography: null });
    } else {
      const current = config.merchantTypography ?? { ...config.typography };
      onChange({
        ...config,
        merchantTypography: { ...current, ...update },
      });
    }
  };
  
  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
      {/* ========== HEADER ========== */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Ribbon className="h-4 w-4 text-primary" />
          <Label className="text-sm font-medium">Ribbon</Label>
        </div>
        <VisibilityChip enabled={structureEnabled} />
      </div>
      
      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
        <Info className="h-3 w-3" />
        Visibility is controlled in Structure. Configure ribbon content + typography here.
      </p>
      
      {/* ========== LIVE PREVIEW ========== */}
      <div className="p-3 bg-background rounded border">
        <Label className="text-[10px] text-muted-foreground mb-2 block">Live Preview</Label>
        <div className="flex flex-col items-start gap-2">
          {/* Ribbon preview */}
          <div 
            className={cn(
              "px-3 py-1.5 rounded text-white font-medium",
              !ribbonColorHex && "bg-emerald-600", // Fallback class only when no resolved color
              !structureEnabled && "opacity-50"
            )}
            style={{
              ...(ribbonColorHex ? { backgroundColor: ribbonColorHex } : {}),
              fontFamily: config.typography.fontFamily !== "inherit" 
                ? config.typography.fontFamily 
                : undefined,
              fontSize: `${config.typography.fontSize}px`,
              fontWeight: config.typography.fontWeight,
            }}
          >
            {renderedText || "BONUS $20"}
          </div>
          
          {/* Merchant subline preview (if enabled) */}
          {config.merchantSubline.enabled && (
            <div 
              className="flex items-center gap-1.5 text-muted-foreground"
              style={{
                fontFamily: effectiveMerchantTypo.fontFamily !== "inherit" 
                  ? effectiveMerchantTypo.fontFamily 
                  : undefined,
                fontSize: `${effectiveMerchantTypo.fontSize}px`,
                fontWeight: effectiveMerchantTypo.fontWeight,
              }}
            >
              {config.merchantSubline.showLogo && (
                <div className="w-4 h-4 bg-muted rounded-sm flex items-center justify-center text-[8px]">
                  Logo
                </div>
              )}
              {config.merchantSubline.showName && (
                <span className="text-xs">{merchantSublineName || "Merchant"}</span>
              )}
            </div>
          )}
          
          {!structureEnabled && (
            <span className="text-[10px] text-muted-foreground">(Disabled in Structure)</span>
          )}
        </div>
      </div>
      
      {/* ========== RENDERED OUTPUT (read-only) ========== */}
      <div className="p-2 bg-muted/50 rounded text-xs space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Rendered output:</span>
          <code className="font-mono text-foreground">{renderedText || "—"}</code>
        </div>
        {config.merchantSubline.enabled && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Merchant subline:</span>
            <span className="text-foreground">
              {config.merchantSubline.showLogo && config.merchantSubline.showName
                ? `${merchantSublineName || "Merchant"} (logo + name)`
                : config.merchantSubline.showLogo
                  ? "Logo only"
                  : config.merchantSubline.showName
                    ? merchantSublineName || "Name only"
                    : "None"}
            </span>
          </div>
        )}
        <div className="text-[10px] text-muted-foreground">
          Merchant source: promo_merchant_name → merchant
        </div>
      </div>
      
      <Separator />
      
      {/* ========== PRIMARY LINE CONTROLS ========== */}
      <div className="space-y-3">
        <Label className="text-xs font-medium">Primary Line (Ribbon Text)</Label>
        
        {/* Deal Type Label */}
        <div className="p-3 bg-background rounded border space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Deal Type Label</Label>
            <Switch
              checked={config.primary.showDealTypeLabel}
              onCheckedChange={(v) => updatePrimary({ showDealTypeLabel: v })}
            />
          </div>
          
          {config.primary.showDealTypeLabel && (
            <div className="space-y-2 pl-2 border-l-2 border-muted">
              <RadioGroup
                value={config.primary.labelMode}
                onValueChange={(v) => updatePrimary({ labelMode: v as "default" | "custom" })}
                className="flex gap-4"
              >
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="default" id="label-default" />
                  <Label htmlFor="label-default" className="text-xs cursor-pointer">Default</Label>
                </div>
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="custom" id="label-custom" />
                  <Label htmlFor="label-custom" className="text-xs cursor-pointer">Custom</Label>
                </div>
              </RadioGroup>
              
              {config.primary.labelMode === "custom" && (
                <DebouncedLabelInput
                  value={config.primary.labelOverride}
                  onChange={(v) => updatePrimary({ labelOverride: v })}
                />
              )}
            </div>
          )}
        </div>
        
        {/* Amount */}
        <div className="p-3 bg-background rounded border space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Amount</Label>
            <Switch
              checked={config.primary.showAmount}
              onCheckedChange={(v) => updatePrimary({ showAmount: v })}
            />
          </div>
          
          {config.primary.showAmount && (
            <div className="pl-2 border-l-2 border-muted">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground">Derived value:</span>
                <code className="font-mono text-xs bg-muted px-1 rounded">
                  {derivedAmount || "—"}
                </code>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Derived from deal economics; not editable.
              </p>
            </div>
          )}
        </div>
        
        {/* Separator */}
        {config.primary.showDealTypeLabel && config.primary.showAmount && (
          <div className="p-3 bg-background rounded border">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Separator</Label>
              <Select
                value={config.primary.separator}
                onValueChange={(v) => updatePrimary({ separator: v as "space" | "bullet" | "dash" })}
              >
                <SelectTrigger className="h-7 w-32 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEPARATOR_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
      
      <Separator />
      
      {/* ========== MERCHANT SUBLINE ========== */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">Merchant Subline (below ribbon)</Label>
          <Switch
            checked={config.merchantSubline.enabled}
            onCheckedChange={(v) => updateMerchantSubline({ enabled: v })}
          />
        </div>
        
        {config.merchantSubline.enabled && (
          <div className="p-3 bg-background rounded border space-y-2">
            <p className="text-[10px] text-muted-foreground">
              Shows where you buy the gift card (promo merchant).
            </p>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="subline-logo"
                  checked={config.merchantSubline.showLogo}
                  onCheckedChange={(v) => updateMerchantSubline({ showLogo: v === true })}
                />
                <Label htmlFor="subline-logo" className="text-xs cursor-pointer">Show logo</Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="subline-name"
                  checked={config.merchantSubline.showName}
                  onCheckedChange={(v) => updateMerchantSubline({ showName: v === true })}
                />
                <Label htmlFor="subline-name" className="text-xs cursor-pointer">Show name</Label>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <Separator />
      
      {/* ========== TYPOGRAPHY ========== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Type className="h-3.5 w-3.5 text-muted-foreground" />
          <Label className="text-xs font-medium">Typography</Label>
        </div>
        
        {/* Ribbon Typography */}
        <div className="p-3 bg-background rounded border space-y-2">
          <Label className="text-[10px] text-muted-foreground">Ribbon (primary line)</Label>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px]">Font</Label>
              <Select 
                value={config.typography.fontFamily} 
                onValueChange={(v) => updateTypography({ fontFamily: v })}
              >
                <SelectTrigger className="h-7 text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_FAMILIES.map((f) => (
                    <SelectItem key={f.value} value={f.value} className="text-xs">
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px]">Size</Label>
              <Input
                type="number"
                value={config.typography.fontSize}
                onChange={(e) => updateTypography({ fontSize: parseInt(e.target.value) || 14 })}
                className="h-7 text-[11px]"
                min={8}
                max={24}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px]">Weight</Label>
              <Select 
                value={String(config.typography.fontWeight)} 
                onValueChange={(v) => updateTypography({ fontWeight: parseInt(v) })}
              >
                <SelectTrigger className="h-7 text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_WEIGHTS.map((w) => (
                    <SelectItem key={w.value} value={String(w.value)} className="text-xs">
                      {w.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        {/* Merchant Typography */}
        {config.merchantSubline.enabled && (
          <div className="p-3 bg-background rounded border space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] text-muted-foreground">Merchant (subline)</Label>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="inherit-typo"
                  checked={usesInheritedTypo}
                  onCheckedChange={(v) => {
                    if (v) {
                      updateMerchantTypography(null);
                    } else {
                      updateMerchantTypography({ ...config.typography });
                    }
                  }}
                />
                <Label htmlFor="inherit-typo" className="text-[10px] cursor-pointer">
                  Use ribbon typography
                </Label>
              </div>
            </div>
            
            {!usesInheritedTypo && (
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px]">Font</Label>
                  <Select 
                    value={effectiveMerchantTypo.fontFamily} 
                    onValueChange={(v) => updateMerchantTypography({ fontFamily: v })}
                  >
                    <SelectTrigger className="h-7 text-[11px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_FAMILIES.map((f) => (
                        <SelectItem key={f.value} value={f.value} className="text-xs">
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px]">Size</Label>
                  <Input
                    type="number"
                    value={effectiveMerchantTypo.fontSize}
                    onChange={(e) => updateMerchantTypography({ fontSize: parseInt(e.target.value) || 12 })}
                    className="h-7 text-[11px]"
                    min={8}
                    max={24}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px]">Weight</Label>
                  <Select 
                    value={String(effectiveMerchantTypo.fontWeight)} 
                    onValueChange={(v) => updateMerchantTypography({ fontWeight: parseInt(v) })}
                  >
                    <SelectTrigger className="h-7 text-[11px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_WEIGHTS.map((w) => (
                        <SelectItem key={w.value} value={String(w.value)} className="text-xs">
                          {w.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Re-export helpers from ribbonViewModel for external use
export { deriveRibbonAmount } from "@/lib/deal-cards/ribbonViewModel";

export default RibbonComposer;
